import { useMemo } from 'react';
import { useProgress } from '../context/ProgressContext.jsx';
import { isProfileComplete } from '../utils/profileForm.js';

export default function useOnboardingStatus({ token, family, member }) {
  const {
    error,
    hasResolvedProgress,
    isLoading: isProgressLoading,
    progress,
    refreshProgress,
    status
  } = useProgress();

  const hasPrerequisites = Boolean(token && family && member);
  const isLoading = hasPrerequisites && !hasResolvedProgress && isProgressLoading;

  const derived = useMemo(() => {
    const categories = progress?.perCategory || [];
    const profileComplete = isProfileComplete(member);
    const hasStartedQuestionnaire = categories.some((category) => category.answeredQuestionsInCategory > 0);
    const hasCompletedCategory = categories.some((category) => category.percent === 100);
    const allCategoriesCompleted = categories.length > 0 && categories.every((category) => category.percent === 100);
    const completedCategoriesCount = categories.filter((category) => category.percent === 100).length;
    const totalAnsweredQuestions = categories.reduce(
      (sum, category) => sum + (category.answeredQuestionsInCategory || 0),
      0
    );
    const totalQuestions = categories.reduce(
      (sum, category) => sum + (category.totalQuestionsInCategory || 0),
      0
    );
    const overallPercent = totalQuestions > 0 ? Math.round((totalAnsweredQuestions / totalQuestions) * 100) : 0;

    const nextStartedCategory = categories.find(
      (category) => category.answeredQuestionsInCategory > 0 && category.percent < 100
    );
    const nextFreshCategory = categories.find((category) => category.answeredQuestionsInCategory === 0);
    const nextIncompleteCategory = categories.find((category) => category.percent < 100);
    const nextCategoryId =
      nextStartedCategory?.categoryId ||
      nextFreshCategory?.categoryId ||
      nextIncompleteCategory?.categoryId ||
      categories[0]?.categoryId ||
      null;

    return {
      profileComplete,
      hasStartedQuestionnaire,
      hasCompletedCategory,
      allCategoriesCompleted,
      completedCategoriesCount,
      totalAnsweredQuestions,
      totalQuestions,
      overallPercent,
      nextCategoryId,
      isOnboardingComplete: profileComplete && hasCompletedCategory
    };
  }, [member, progress]);

  return {
    progress,
    isLoading,
    status,
    error,
    hasResolvedProgress,
    refreshProgress,
    ...derived
  };
}
